import { appOn, type ChatMessage, threadKey } from "@rhema/client";
import { Button, ChevronLeftIcon, cn, EmptyState, focusRing, UiLink } from "@rhema/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ChatBoundary } from "../app/Crash";
import { keyHint, MessageBox, type MessageBoxHandle } from "../conversation/MessageBox";
import { ContentProvider } from "../conversation/MessageContent";
import { MessageList } from "../conversation/MessageList";
import { useMessageMenu } from "../conversation/messageMenu";
import { useChatMedia } from "../conversation/useChatMedia";
import { EmailComposer } from "../links/EmailComposer";
import { useFeedback } from "../menus/Feedback";
import { ASK_RECENT } from "../queravel/ask";
import { replyKey, useDraftedReply, useQueravelPanel } from "../queravel/panel";
import { useQueravelOffered } from "../queravel/QueravelChat";
import { useClient, useClientState, useMe } from "../session/session";

/**
 * One thread: the message it hangs on, its replies and a box to reply. On desktop it sits beside
 * the channel. On phones it is a screen of its own. Replies get the same actions as messages in
 * the channel, including Queravel, Malakh and Penielly where the space allows them.
 */
type ThreadPanelProps = {
  chatId: string;
  rootId: string;
  variant: "panel" | "phone";
  onClose?: () => void;
  backHref?: string;
};

export function ThreadPanel(props: ThreadPanelProps) {
  return (
    <ChatBoundary resetKey={`${props.chatId}#${props.rootId}`}>
      <Thread {...props} />
    </ChatBoundary>
  );
}

function Thread({ chatId, rootId, variant, onClose, backHref }: ThreadPanelProps) {
  const client = useClient();
  const state = useClientState();
  const me = useMe();
  const feedback = useFeedback();
  const chat = state.chats[chatId];
  const root = state.messages[chatId]?.find((m) => m.id === rootId);
  const replies = state.messages[threadKey(chatId, rootId)] ?? [];
  const [problem, setProblem] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [emailFor, setEmailFor] = useState<ChatMessage[] | null>(null);
  const box = useRef<MessageBoxHandle>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const log = useRef<HTMLDivElement>(null);
  // A link to a reply opens its thread with ?reply=, which picks the reply out for a moment.
  const [params, setParams] = useSearchParams();
  const linked = params.get("reply");
  const [highlight, setHighlight] = useState<string | null>(null);
  const queravelOffered = useQueravelOffered();
  const queravel = useQueravelPanel();
  // A reply Queravel drafted about this thread comes back to this box.
  useDraftedReply(replyKey(chatId, rootId), (text) => box.current?.fill(text));

  // A thread opened from a link loads its channel too, for the message it hangs on.
  useEffect(() => {
    void client.openChat(chatId).catch(() => undefined);
    void client.openThread(chatId, rootId).catch(() => setProblem("The replies could not be loaded."));
  }, [client, chatId, rootId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrolls when a reply arrives
  useEffect(() => {
    if (!highlight && !linked) bottom.current?.scrollIntoView?.({ block: "end" });
  }, [replies.length]);

  // The linked reply comes into view once it has loaded.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the reply arrives
  useEffect(() => {
    if (!linked || !replies.some((m) => m.id === linked)) return;
    const p = new URLSearchParams(params);
    p.delete("reply");
    setParams(p, { replace: true });
    setHighlight(linked);
  }, [linked, replies]);
  useLayoutEffect(() => {
    if (!highlight) return;
    log.current
      ?.querySelector<HTMLElement>(`[data-message-id="${highlight}"]`)
      ?.scrollIntoView?.({ block: "center" });
    const t = window.setTimeout(() => setHighlight(null), 2500);
    return () => window.clearTimeout(t);
  }, [highlight]);

  const all = [...(root ? [root] : []), ...replies];
  const peniellyOn = chat ? appOn(state, chat, "penielly") : false;
  const chatMedia = useChatMedia({
    chat,
    meId: me.id,
    messages: all,
    recipient: chat ? `#${chat.title}` : "",
    attachOpen: attaching,
    closeAttach: () => setAttaching(false),
    replyTo: null,
    onSent: () => undefined,
    toast: setProblem,
    threadRoot: rootId,
    penielly: peniellyOn,
  });

  const askableHere = Boolean(chat && queravelOffered && appOn(state, chat, "queravel"));
  const menu = useMessageMenu({
    chatId,
    messages: all,
    desktop: variant === "panel",
    inThread: { rootId },
    onEdit: setEditing,
    ...(askableHere && chat
      ? {
          onAsk: (m: ChatMessage) =>
            queravel.show({ about: { chatId, title: `#${chat.title}`, threadRoot: rootId, messages: [m] } }),
        }
      : {}),
    ...(chat && appOn(state, chat, "malakh") ? { onEmail: (m: ChatMessage) => setEmailFor([m]) } : {}),
  });

  if (!chat) return <EmptyState title="Thread not found" className="flex-1" />;

  const askable = queravelOffered && appOn(state, chat, "queravel");
  const readable = all.filter((m) => m.kind === "message" && m.seq > 0);
  // Queravel reads the message the thread hangs on and its recent replies, or the ones picked.
  const askAbout = (picked: ChatMessage[]) =>
    queravel.show({ about: { chatId, title: `#${chat.title}`, threadRoot: rootId, messages: picked } });
  const askThread = askable ? () => askAbout(readable.slice(-ASK_RECENT)) : undefined;

  // The server's count, or the replies already here if it has not caught up yet.
  const count = Math.max(
    root?.replyCount ?? 0,
    replies.filter((m) => m.kind === "message" && !m.deleted && m.content?.type !== "notice").length,
  );
  return (
    <div
      className={cn("relative flex min-h-0 flex-1 flex-col bg-chat", variant === "panel" && "h-full")}
      {...chatMedia.container}
    >
      <header className="flex flex-none items-center gap-3 border-b border-line bg-linen px-5 py-3.5">
        {backHref && (
          <UiLink
            href={backHref}
            aria-label={`Back to #${chat.title}`}
            className={cn("-ml-2 rounded-full p-1 text-ink", focusRing)}
          >
            <ChevronLeftIcon size={24} />
          </UiLink>
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <h2 className="m-0 text-[17px] font-bold">Thread</h2>
          <span className="truncate text-[13px] text-ink-secondary">#{chat.title}</span>
        </span>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div ref={log} role="log" aria-label="Replies" className="flex min-h-full flex-col gap-1 px-4 py-4">
          {problem && (
            <p
              role="alert"
              className="m-0 mb-2 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet"
            >
              {problem}
            </p>
          )}
          <ContentProvider value={chatMedia.content}>
            {root ? (
              <MessageList
                chat={chat}
                messages={[root]}
                meId={me.id}
                hasOlder
                typing={[]}
                describe={(s) => client.describeSystem(s, chat)}
                onRetry={() => undefined}
                actionProps={menu.holdProps}
                starred={menu.starred}
                toolbar={menu.toolbar}
              />
            ) : (
              <p className="m-0 text-sm text-ink-meta">Loading the first message.</p>
            )}
            <div className="my-2 flex items-center gap-3 text-[13px] text-ink-secondary">
              <span className="h-px flex-1 bg-line" />
              {count === 1 ? "1 reply" : `${count} replies`}
              <span className="h-px flex-1 bg-line" />
            </div>
            <MessageList
              chat={chat}
              messages={replies}
              meId={me.id}
              hasOlder
              typing={[]}
              describe={(s) => client.describeSystem(s, chat)}
              onRetry={(m) => m.clientId && void client.retry(chatId, m.clientId, rootId)}
              actionProps={menu.holdProps}
              highlightId={highlight}
              starred={menu.starred}
              toolbar={menu.toolbar}
            />
          </ContentProvider>
          <div ref={bottom} />
        </div>
      </div>
      <MessageBox
        key={`${chatId}#${rootId}`}
        ref={box}
        chat={chat}
        draftKey={threadKey(chatId, rootId)}
        threadRoot={rootId}
        desktop={variant === "panel"}
        placeholder="Reply"
        messages={replies}
        replyTo={null}
        onReplyTo={() => undefined}
        editing={editing}
        onEditing={setEditing}
        onAttach={() => setAttaching(true)}
        onFiles={chatMedia.attach}
        onAskQueravel={askThread}
        schedulable
        media={chatMedia.composer}
        hint={variant === "panel" ? keyHint(Boolean(askThread)) : undefined}
        toast={setProblem}
      />

      {menu.overlays}
      {emailFor && (
        <EmailComposer
          open
          chat={chat}
          chatTitle={`#${chat.title}`}
          messages={emailFor}
          meId={me.id}
          onClose={() => setEmailFor(null)}
          onDone={(note) => {
            setEmailFor(null);
            feedback.notify(note);
          }}
        />
      )}
      {chatMedia.overlays}
    </div>
  );
}
